import type { ReactNode } from "react";

/**
 * One labelled value of a {@link RegistrationSummary}.
 *
 * @since 1.3.0
 * @example
 * ```ts
 * const row: RegistrationSummaryRow = { label: "Geburtsdatum", value: "01.10.2000" };
 * ```
 */
export interface RegistrationSummaryRow {
  readonly label: string;
  readonly value: ReactNode;
  readonly testId?: string;
}

/**
 * Read-only list of the patient's registration data in kiosk type sizes:
 * 16px labels in foreground colour above 20px values, stacked so long names
 * and insurer names wrap instead of squeezing a label column (KC-09, KC-76,
 * KC-80). Sized to its content.
 *
 * @since 1.3.0
 * @example
 * ```tsx
 * <RegistrationSummary rows={[{ label: t("…dob"), value: "01.10.2000" }]} />
 * ```
 */
export function RegistrationSummary({
  rows,
  testId,
}: {
  readonly rows: readonly RegistrationSummaryRow[];
  readonly testId?: string;
}) {
  return (
    <dl
      data-testid={testId}
      className="m-0 grid min-w-0 gap-0 divide-y rounded-3xl border bg-card"
    >
      {rows.map((row) => (
        <div key={row.label} className="flex min-w-0 flex-col gap-1 px-6 py-4">
          <dt className="text-base font-medium text-foreground">{row.label}</dt>
          <dd
            data-testid={row.testId}
            className="m-0 text-xl font-semibold break-words text-foreground"
          >
            {row.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}
